import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback
} from 'react';
import {
  useJsApiLoader,
  GoogleMap,
  OverlayViewF
} from '@react-google-maps/api';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';
import { createPortal } from 'react-dom';
import { getGoogleApiKey } from '../../utils/helpers/common';
import { getLangDefinition } from '../../utils/helpers';
import useScreenSize from '../../hooks/useScreenSize';
import { GoogleMapContainer } from './styled';
import { ItemsMapMarker } from './ItemMapMarker';
import { ItemsMapProps, Marker } from './types';
import { MapItemsModal } from './MapItemModal';
import { MapSidePanel } from './MapSidePanel';
import {
  getMapOptions,
  getMapPadding,
  getShrunkBoundsFromMap,
  isMovementSignificant,
  mapBoundsToGoogleBounds,
  panToMarkerWithDesktopOffset
} from '../../utils/helpers/map';
import { MapBounds } from '../../graphql/graphql';
import { useClient } from '../../hooks/useClient';
import { ItemsMapPlaceholder } from './ItemsMapPlaceholder';

const MAX_ZOOM_VALUE = 21;
const PIXEL_THRESHOLD = 15;

export const ItemsMap: React.FC<ItemsMapProps> = ({
  title,
  zoom,
  map,
  mapBounds,
  mapMarkers,
  activeSelection,
  setMap,
  setZoom,
  setActiveSelection,
  children,
  onBoundsChange
}) => {
  const {
    i18n: { language }
  } = useTranslation();
  const { isSmall, isSmallOrLargeDesktop } = useScreenSize();
  const isClient = useClient();

  const [isFullscreenMap, setIsFullscreenMap] = useState(false);
  const [fullScreenElement, setFullscreenElement] = useState<Element | null>(
    null
  );
  const prevBoundsRef = useRef<MapBounds | null>(null);
  const lastQueryPixelRef = useRef<google.maps.Point | null>(null);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: getGoogleApiKey(),
    language: getLangDefinition(language),
    region: 'AZ'
  });

  const MAP_HEIGHT = !isClient || isSmall ? 450 : 660;

  useEffect(() => {
    if (!map || !mapBounds || !isLoaded) return;

    const prevBounds = prevBoundsRef.current;

    const isSameBounds =
      prevBounds &&
      prevBounds.topLeft.latitude === mapBounds.topLeft.latitude &&
      prevBounds.topLeft.longitude === mapBounds.topLeft.longitude &&
      prevBounds.bottomRight.latitude === mapBounds.bottomRight.latitude &&
      prevBounds.bottomRight.longitude === mapBounds.bottomRight.longitude;

    if (isSameBounds) return;

    const bounds = mapBoundsToGoogleBounds(mapBounds);
    const padding = getMapPadding({ isSmall, mapHeight: MAP_HEIGHT });

    map.fitBounds(bounds, padding);

    prevBoundsRef.current = mapBounds;
  }, [map, mapBounds, isSmall, MAP_HEIGHT, isLoaded]);

  useEffect(() => {
    const handleFullScreenChange = () => {
      setIsFullscreenMap(!!document.fullscreenElement);
      setFullscreenElement(document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullScreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
    };
  }, []);

  const onCloseSidePanel = useCallback(
    () => setActiveSelection(null),
    [setActiveSelection]
  );

  const handleMarkerClick = useCallback(
    (marker: Marker) => {
      if (!map) return;
      const isClusterMarker = marker.type === 'ClusterMapMarker';
      const nextZoom = (zoom ?? 10) + 2;

      if (isClusterMarker && nextZoom < MAX_ZOOM_VALUE) {
        onCloseSidePanel();
        map.panTo(marker.position);
        map.setZoom(nextZoom);
        return;
      }

      if (activeSelection?.id !== marker.id) onCloseSidePanel();
      setActiveSelection(marker);
      panToMarkerWithDesktopOffset(map, marker.position, {
        isSmallOrLargeDesktop,
        offsetRatio: 0.15
      });
    },
    [
      map,
      zoom,
      activeSelection,
      setActiveSelection,
      isSmallOrLargeDesktop,
      onCloseSidePanel
    ]
  );

  const handleIdle = useCallback(() => {
    if (!map) return;
    const projection = map.getProjection();
    const center = map.getCenter();
    const currentZoom = map.getZoom();

    if (!projection || !center || currentZoom === undefined) return;

    const currentPixelPoint = projection.fromLatLngToPoint(center);
    if (!currentPixelPoint) return;

    let shouldUpdate = false;
    if (!lastQueryPixelRef.current || zoom !== currentZoom) {
      shouldUpdate = true;
    } else {
      shouldUpdate = isMovementSignificant(
        lastQueryPixelRef.current,
        currentPixelPoint,
        currentZoom,
        PIXEL_THRESHOLD
      );
    }

    if (shouldUpdate) {
      lastQueryPixelRef.current = currentPixelPoint;
      const bounds = getShrunkBoundsFromMap(map);
      onBoundsChange?.(bounds);
      setZoom(currentZoom);
    }
  }, [map, zoom, setZoom]);

  const debouncedHandleIdle = useMemo(
    () => debounce(handleIdle, 300),
    [handleIdle]
  );

  const containerStyle = useMemo(
    () => ({
      width: '100%',
      height: `${MAP_HEIGHT}px`
    }),
    [MAP_HEIGHT]
  );

  const mapOptions = useMemo(
    () =>
      isLoaded ? getMapOptions(isSmall, isSmallOrLargeDesktop) : undefined,
    [isLoaded, isSmall, isSmallOrLargeDesktop]
  );

  const markersList = useMemo(() => {
    return mapMarkers?.map(marker => (
      <OverlayViewF
        key={marker.id}
        position={marker.position}
        mapPaneName="overlayMouseTarget"
      >
        <ItemsMapMarker
          marker={marker}
          isActive={activeSelection?.id === marker.id}
          onMarkerClick={handleMarkerClick}
        />
      </OverlayViewF>
    ));
  }, [mapMarkers, activeSelection?.id, handleMarkerClick]);

  if (!isLoaded && !loadError) {
    return <ItemsMapPlaceholder />;
  }

  const modalContainer =
    isFullscreenMap && fullScreenElement ? fullScreenElement : undefined;

  const sidePanel = (
    <MapSidePanel onClose={onCloseSidePanel} isFullScreen={isFullscreenMap}>
      {children}
    </MapSidePanel>
  );

  return (
    <GoogleMapContainer>
      <GoogleMap
        mapContainerStyle={containerStyle}
        options={mapOptions}
        onLoad={loadedMap => setMap(loadedMap)}
        onIdle={debouncedHandleIdle}
      >
        {markersList}
      </GoogleMap>

      {isSmallOrLargeDesktop &&
        !!activeSelection &&
        (isFullscreenMap && fullScreenElement
          ? createPortal(sidePanel, fullScreenElement)
          : sidePanel)}

      {!isSmallOrLargeDesktop && !!activeSelection && (
        <MapItemsModal
          title={title}
          isOpen={!!activeSelection}
          onClose={onCloseSidePanel}
          container={modalContainer}
        >
          {children}
        </MapItemsModal>
      )}
    </GoogleMapContainer>
  );
};
